import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { useFetch } from "@/lib/content";

const STATUSES = ["new", "contacted", "qualified", "closed"];

export default function LeadsAdmin() {
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [nonce, setNonce] = useState(0);
  const qs = `?${source ? `source=${source}&` : ""}${status ? `status=${status}&` : ""}_=${nonce}`;
  const { data: leads } = useFetch(`/admin/leads${qs}`);
  const reload = () => setNonce((n) => n + 1);

  const updateStatus = async (id, next) => {
    try {
      await api.put(`/admin/leads/${id}`, { status: next });
      toast.success("Status updated");
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl font-medium tracking-tight">Leads</h1>
        <a
          href={`${API}/admin/leads/export`}
          data-testid="export-csv"
          className="inline-flex items-center gap-2 bg-obsidian px-5 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-paper transition-colors hover:bg-progress"
        >
          <Download size={14} /> Export CSV
        </a>
      </div>

      <div className="mt-8 flex flex-wrap gap-6">
        <label className="flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slategray">Source</span>
          <select data-testid="leads-source-filter" value={source} onChange={(e) => setSource(e.target.value)} className="border border-obsidian/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-progress">
            <option value="">All</option>
            <option value="discovery">Discovery call</option>
            <option value="contact">Contact form</option>
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slategray">Status</span>
          <select data-testid="leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-obsidian/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-progress">
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>

      {leads === null ? (
        <p className="mt-10 text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      ) : leads.length === 0 ? (
        <div className="mt-10 border border-ash p-14 text-center" data-testid="leads-empty">
          <p className="font-serif text-2xl">No leads in this view.</p>
          <p className="mt-2 text-sm text-slategray">Form submissions from the website will land here — and be emailed to you.</p>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[900px] border border-ash text-left text-sm">
            <thead>
              <tr className="border-b border-ash bg-ash/40 text-[11px] uppercase tracking-[0.16em] text-slategray">
                <th className="p-4 font-bold">Contact</th>
                <th className="p-4 font-bold">Company</th>
                <th className="p-4 font-bold">Qualification</th>
                <th className="p-4 font-bold">Message</th>
                <th className="p-4 font-bold">Source</th>
                <th className="p-4 font-bold">Alert</th>
                <th className="p-4 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-ash/70 last:border-b-0 align-top" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4">
                    <p className="font-semibold">{l.name}</p>
                    <p className="text-[12px] text-slategray">{l.email}</p>
                    {l.phone && <p className="text-[12px] text-slategray">{l.phone}</p>}
                  </td>
                  <td className="p-4">{l.company || "—"}</td>
                  <td className="p-4 text-[12px] leading-relaxed text-slategray">
                    <p><span className="font-bold text-obsidian">Size:</span> {l.company_size || "—"}</p>
                    <p><span className="font-bold text-obsidian">Revenue:</span> {l.revenue_range || "—"}</p>
                    <p><span className="font-bold text-obsidian">Pain:</span> {l.pain_area || "—"}</p>
                    <p><span className="font-bold text-obsidian">Timeline:</span> {l.timeline || "—"}</p>
                  </td>
                  <td className="max-w-[220px] p-4 text-[13px] text-slategray">{l.message || "—"}</td>
                  <td className="p-4"><span className="bg-ash px-2 py-1 text-[11px] font-bold uppercase">{l.source}</span></td>
                  <td className="p-4">
                    {l.alert ? (
                      <span
                        data-testid={`lead-alert-${l.id}`}
                        title={l.alert.status === "sent" ? `Sent to ${l.alert.to}` : l.alert.error}
                        className={`px-2 py-1 text-[11px] font-bold uppercase ${l.alert.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}
                      >
                        {l.alert.status === "sent" ? "Email sent" : "Email failed"}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slategray">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    <select
                      data-testid={`lead-status-${l.id}`}
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className="border border-obsidian/20 bg-white px-3 py-2 text-[12px] outline-none focus:border-progress"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
